import clsx from "clsx";
import dayjs from "dayjs";

import { DateTimeTz, Icon } from "@web-app/components";
import { OrderTimeline } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";

interface ReportActivityProps {
  item: OrderTimeline;
  showTimelineBar: boolean;
  timezone?: keyof typeof timeZoneMap;
}

function ReportTemplate({
  reportDescription,
  type,
  isResolved,
}: {
  isResolved: boolean;
  reportDescription: string;
  type: string;
}) {
  if (type === "") return null;

  function getResolveUnresolvedText() {
    return isResolved ? "Resolved Report" : "Unresolved Report";
  }

  if (type === "OTHER_REASON") {
    return (
      <p className="first-letter:uppercase">
        Other - {reportDescription || getResolveUnresolvedText()}
      </p>
    );
  }

  if (type === "CUSTOM") {
    return (
      <p className="first-letter:uppercase">
        {reportDescription || getResolveUnresolvedText()}
      </p>
    );
  }

  const typeToValue = type.split("_").join(" ").toLowerCase();
  const description = reportDescription.toLocaleLowerCase();
  let showDescription = false;
  if (
    reportDescription != "" &&
    typeToValue.toLowerCase() !== description.toLowerCase()
  ) {
    showDescription = true;
  }

  return (
    <>
      <p className="first-letter:uppercase">{typeToValue}</p>
      <p className="mt-1 text-[10px] md:text-xs text-gray-600 first-letter:uppercase">
        {showDescription && <>{description}</>}
      </p>
    </>
  );
}

export default function ReportActivity({
  item,
  showTimelineBar,
  timezone,
}: ReportActivityProps) {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);

    if (timezone) {
      return (
        <DateTimeTz start={date} timezone={timezone} presentation="timeline" />
      );
    }

    return dayjs(date).format("MMM DD, YYYY, hh:mm A [GMT]Z");
  };

  const isResolveAction = item.updatedValue === "resolved";
  const reportDescription = item.report?.description || "";
  const createdBy =
    item.createdBy?.name ||
    item.report?.reporterName ||
    item.report?.reporterEmail ||
    "Customer";
  const createdAt = new Date(item.createdAt).toISOString();

  return (
    <li>
      <div className="relative">
        <div className="flex items-start space-x-3">
          <li className="relative flex gap-x-2 md:gap-x-4">
            <div
              className={clsx(
                showTimelineBar ? "-bottom-6" : "h-6",
                "absolute left-0 top-0 flex w-6 justify-center",
              )}
            >
              <div className="w-px bg-gray-200" />
            </div>

            <div className="relative flex h-7 w-6 flex-none items-center justify-center bg-white">
              <Icon
                name="flag"
                className={clsx(
                  "h-3 md:h-4",
                  isResolveAction ? "text-green-500" : "text-red-500",
                )}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div>
                <p
                  className={clsx(
                    "text-[10px] md:text-xs font-medium",
                    isResolveAction ? "text-green-700" : "text-red-700",
                  )}
                >
                  <ReportTemplate
                    type={item.report?.type || ""}
                    isResolved={isResolveAction}
                    reportDescription={reportDescription}
                  />
                </p>
                <div className="mt-2 text-[10px] md:text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">
                    {formatTime(createdAt)} by {createdBy}
                  </span>
                </div>
              </div>
            </div>
          </li>
        </div>
      </div>
    </li>
  );
}
